// The parts of an answer (ERP-135): Markdown, and the two fenced blocks the assistant is told to
// write — ```chart and ```followups, each holding JSON. Kept apart from the components so it can be
// tested on its own.

export interface ChartSpec {
  type: "bar" | "line" | "pie";
  title?: string;
  labels: string[];
  series: { name?: string; values: number[] }[];
}

export type Part =
  | { kind: "md"; text: string }
  | { kind: "chart"; spec: ChartSpec }
  | { kind: "followups"; items: string[] };

const FENCE = /```(chart|followups)[^\n]*\n([\s\S]*?)(?:```|$)/g;

/** The answer split into Markdown, charts and follow-ups. An unfinished block (still streaming) is left out. */
export function split(text: string): Part[] {
  const parts: Part[] = [];
  let last = 0;
  for (const m of text.matchAll(FENCE)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ kind: "md", text: text.slice(last, at) });
    last = at + m[0].length;
    // Still being written: wait for the closing fence before showing anything.
    if (!m[0].endsWith("```")) continue;
    try {
      const json = JSON.parse(m[2] ?? "");
      if (m[1] === "followups" && Array.isArray(json))
        parts.push({ kind: "followups", items: json.map(String).filter(Boolean).slice(0, 4) });
      else if (m[1] === "chart" && valid(json)) parts.push({ kind: "chart", spec: json });
      else parts.push({ kind: "md", text: m[0] });
    } catch {
      parts.push({ kind: "md", text: m[0] });
    }
  }
  if (last < text.length) parts.push({ kind: "md", text: text.slice(last) });
  return parts.filter((p) => p.kind !== "md" || p.text.trim());
}

// biome-ignore lint/suspicious/noExplicitAny: checking JSON the model wrote
function valid(j: any): j is ChartSpec {
  return (
    j &&
    ["bar", "line", "pie"].includes(j.type) &&
    Array.isArray(j.labels) &&
    Array.isArray(j.series) &&
    j.series.length > 0 &&
    j.series.every(
      // biome-ignore lint/suspicious/noExplicitAny: as above
      (s: any) =>
        Array.isArray(s.values) &&
        s.values.every((v: unknown) => typeof v === "number" && Number.isFinite(v)),
    )
  );
}
